import type { Immutable } from "immer";
import React from "react";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { ColumnsBlock as ColumnsBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Value = ColumnsBlockValue;
type Column = NonNullable<Value["columns"]>[number];

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/columns-block.html``.
 *
 * Reproduces the legacy ``<section class="columns-block {bg} {theme}
 * {spacing}">`` wrapper with an inner ``<div>`` and one ``<article
 * class="{responsive_visibility}">`` per column. Each column's content is a
 * (shared, already-ported) ``TSIAlignedRichTextBlock``, rendered via the shared
 * React component (which reproduces ``{% include_block column.content %}``).
 */
const ColumnArticle = (
    props: Immutable<{ column: Column }>,
): React.JSX.Element => {
    const column = props.column;
    return (
        <article className={column.responsive_visibility?.visibility ?? ""}>
            {column.content ? (
                <TSIAlignedRichTextBlock value={column.content} />
            ) : null}
        </article>
    );
};

export const ColumnsBlock = (
    props: Immutable<{ value: ColumnsBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const columns = v.columns ?? [];
    return (
        <section
            className={concatClassNames([
                "columns-block",
                v.background_style?.style ?? "",
                v.theme?.theme ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <div>
                {columns.map((column, i) => (
                    <ColumnArticle key={i} column={column} />
                ))}
            </div>
        </section>
    );
};
